import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Box, Button, IconButton, InputAdornment, Link, TextField, Typography } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { LoginMfaChallenge, abortPasskeyCeremony } from '@summa/mfa-login';
import type { MfaChallengeAdapters } from '@summa/mfa-login';
import { getStoredReferral } from '../referralAttribution';
import QixitLogo from './QixitLogo';
import SocialContinueButton, { type SocialContinueSuccess } from './SocialContinueButton';
import ExpressSignup, { type ExpressSignupSuccess } from './ExpressSignup';
import ForgotPasswordDialog from './ForgotPasswordDialog';
import ForgotUsernameDialog from './ForgotUsernameDialog';
import type {
  QixitAuthApi,
  QixitAuthAttribution,
  QixitAuthContext,
  QixitAuthIpChallenge,
  QixitAuthMeta,
  QixitAuthMfaChallenge,
  QixitAuthResult,
  QixitAuthSuccess,
} from './types';
import { errorMessage } from './types';

type Step = 'id' | 'password' | 'confirm' | 'create';

export type QixitAuthPanelProps = {
  api: QixitAuthApi;
  context: QixitAuthContext;
  attribution: QixitAuthAttribution;
  returnUrl?: string;
  title?: string;
  subtitle?: ReactNode;
  defaultMode?: 'login' | 'create';
  variant?: 'page' | 'overlay';
  /** Overlay sizing defaults to compact; a roomy overlay (stay-on-page payout) can opt out. */
  compact?: boolean;
  /** Prefix for data-testid values kept stable for e2e specs (e.g. `paymentfast`). */
  testIdPrefix?: string;
  /** Link shown by express signup when the kill switch is on. */
  fullRegisterUrl?: string;
  onExpressDisabled?: () => void;
  onForgotPassword?: (identifier: string) => void;
  onAuthenticated: (result: QixitAuthSuccess, meta: QixitAuthMeta) => void | Promise<void>;
  footer?: ReactNode;
};

const TERMS_URL = 'https://qixit.com/terms-of-use/';

function looksLikeEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

/**
 * The one Qixit sign-in / account-creation panel. Mailbox /login, mailbox /register,
 * PaymentFast and PurchaseFast all render this, so the flows cannot drift apart.
 */
export default function QixitAuthPanel({
  api,
  context,
  attribution,
  returnUrl,
  title,
  subtitle,
  defaultMode = 'login',
  variant = 'overlay',
  compact,
  testIdPrefix = context,
  fullRegisterUrl,
  onExpressDisabled,
  onForgotPassword,
  onAuthenticated,
  footer,
}: QixitAuthPanelProps) {
  // WordPress opens PurchaseFast and PaymentFast in a 360px-wide popup. Keep that
  // copy of the panel dense so the login fits without a scrollbar.
  const isCheckout = context === 'purchasefast' || context === 'paymentfast';
  const isCompact = isCheckout || (compact ?? variant === 'overlay');
  const fontSize = isCheckout ? '11px' : isCompact ? '11px' : '15px';
  const smallFont = isCheckout ? '9px' : isCompact ? '10px' : '13px';
  const inputStyle = isCompact ? { fontSize, padding: isCheckout ? '3px 6px' : '6px' } : { fontSize };
  const createMode = defaultMode === 'create';

  const [identifier, setIdentifier] = useState('');
  const [step, setStep] = useState<Step>('id');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [mfa, setMfa] = useState<QixitAuthMfaChallenge | null>(null);
  const [ip, setIp] = useState<QixitAuthIpChallenge | null>(null);
  const [ipCode, setIpCode] = useState('');
  const [forgotUsernameOpen, setForgotUsernameOpen] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);

  // A generic register link may arrive with only the referral cookie; never drop it.
  const effectiveAttribution = useMemo<QixitAuthAttribution>(() => {
    if (attribution.referralCode?.trim()) return attribution;
    const stored = getStoredReferral()?.ref?.trim();
    return stored ? { ...attribution, referralCode: stored } : attribution;
  }, [attribution]);

  const run = async (fallback: string, work: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await work();
    } catch (err) {
      setError(errorMessage(err, fallback));
    } finally {
      setBusy(false);
    }
  };

  const continueAuth = async (result: QixitAuthResult, meta: QixitAuthMeta) => {
    if (result.kind === 'mfa') {
      setIp(null);
      setMfa(result);
      return;
    }
    if (result.kind === 'ip') {
      setMfa(null);
      setIpCode('');
      setIp(result);
      return;
    }
    setMfa(null);
    setIp(null);
    await onAuthenticated(result, meta);
  };

  const handleIdentify = () =>
    run('Could not look up that username or email.', async () => {
      setInfo(null);
      const value = identifier.trim();
      const data = await api.post<{ kind: string }>('/api/Auth/identify', { identifier: value });
      if (data.kind === 'known') {
        if (createMode && looksLikeEmail(value)) {
          setInfo('That email already has a Qixit account. Enter your password to sign in.');
        }
        setStep('password');
      } else if (data.kind === 'unknown_email') {
        setStep('confirm');
      } else if (data.kind === 'ambiguous_email') {
        setError('More than one account uses that email. Enter your username.');
        setForgotUsernameOpen(true);
      } else if (createMode && !looksLikeEmail(value)) {
        setError('Enter your email address to create an account.');
      } else {
        setError('No account uses that username.');
      }
    });

  const handlePassword = (e: FormEvent) => {
    e.preventDefault();
    void run('Invalid username or password', async () => {
      const result = await api.login(identifier.trim(), password);
      await continueAuth(result, { method: 'password' });
    });
  };

  const handleSocial = (result: SocialContinueSuccess) =>
    run('Sign-in failed', () =>
      continueAuth(
        { kind: 'success', token: result.accessToken, refreshToken: result.refreshToken, userId: result.userId, username: result.username },
        { method: 'social' }
      )
    );

  const handleExpress = (result: ExpressSignupSuccess) =>
    run('Account creation failed', () =>
      continueAuth(
        { kind: 'success', token: result.accessToken, refreshToken: result.refreshToken, userId: result.userId, username: result.username },
        { method: 'express', isNewAccount: true, isProvisional: result.isProvisional }
      )
    );

  const heading = title ?? (createMode ? 'Create your Qixit account' : 'Login or Account Creation Required');

  const header = (
    <>
      {!isCheckout && <QixitLogo size={variant === 'page' ? 64 : 40} />}
      <Typography
        component="h1"
        sx={{ fontSize: isCheckout ? '12px' : isCompact ? '13px' : '1.5rem', fontWeight: 700, textAlign: 'center', lineHeight: 1.2, mb: isCheckout ? 0.25 : 0.5 }}
        data-testid="qixit-auth-title"
      >
        {heading}
      </Typography>
      {subtitle && (
        <Box sx={{ fontSize: smallFont, color: 'text.secondary', textAlign: 'center', lineHeight: 1.25, mb: isCheckout ? 0.5 : 1 }}>{subtitle}</Box>
      )}
      {error && (
        <Typography color="error" sx={{ fontSize: smallFont, mb: 0.5 }} data-testid="qixit-auth-error">
          {error}
        </Typography>
      )}
    </>
  );

  if (mfa) {
    const adapters: MfaChallengeAdapters<QixitAuthResult> = {
      getPasskeyAuthOptions: (args) => api.getPasskeyAuthOptions(args),
      completeMethod: ({ method, code, assertionResponse }) =>
        api.completeMfa({
          token: mfa.mfaSessionToken,
          method,
          code,
          assertionResponse,
          availableMethods: mfa.availableMethods,
        }),
      requestEmailCode: () => api.requestMfaEmail(mfa.mfaSessionToken),
    };
    return (
      <Box sx={{ width: '100%', textAlign: 'left' }} data-testid="qixit-auth-panel">
        {error && (
          <Typography color="error" sx={{ fontSize: smallFont, mb: 0.5 }}>
            {error}
          </Typography>
        )}
        <LoginMfaChallenge<QixitAuthResult>
          userId={mfa.userId}
          preferredMethod={mfa.preferredMethod}
          availableMethods={mfa.availableMethods}
          availablePasskeys={mfa.availablePasskeys}
          mfaScopeCredentialId={mfa.mfaScopeCredentialId}
          adapters={adapters}
          autoStartKey={mfa.mfaSessionToken}
          onComplete={(result) =>
            void run('MFA verification failed', () =>
              continueAuth(result, { method: 'mfa', mfaScopeCredentialId: mfa.mfaScopeCredentialId })
            )
          }
          onCancel={() => {
            abortPasskeyCeremony();
            setMfa(null);
            setError(null);
          }}
        />
      </Box>
    );
  }

  if (ip) {
    return (
      <Box sx={{ width: '100%' }} data-testid="qixit-auth-panel">
        {header}
        <Typography sx={{ fontSize, fontWeight: 700, mb: 0.5 }}>Email verification required</Typography>
        <Typography sx={{ fontSize: smallFont, mb: 1 }}>
          We noticed a sign-in from a new location. Enter the 6-digit code sent to {ip.maskedEmail || 'your email'}.
        </Typography>
        <Box
          component="form"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            void run('Verification failed', async () => {
              const result = await api.verifyIp({ token: ip.ipVerificationToken, code: ipCode });
              await continueAuth(result, { method: 'password' });
            });
          }}
        >
          <TextField
            label="Email code"
            fullWidth
            size="small"
            autoFocus
            autoComplete="one-time-code"
            value={ipCode}
            onChange={(e) => setIpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            disabled={busy}
            sx={{ mb: 0.5 }}
            inputProps={{ maxLength: 6, style: { fontSize: '14px', letterSpacing: '0.3em', textAlign: 'center' }, 'data-testid': 'qixit-auth-ip-code' }}
          />
          <Button type="submit" variant="contained" fullWidth size="small" disabled={busy || ipCode.length !== 6} sx={{ mb: 0.5, fontSize }}>
            {busy ? 'Verifying…' : 'Verify & continue'}
          </Button>
        </Box>
        <Button
          variant="text"
          size="small"
          onClick={() => {
            setIp(null);
            setIpCode('');
            setError(null);
          }}
          sx={{ fontSize: smallFont, textTransform: 'none' }}
        >
          Back
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', textAlign: 'left' }} data-testid="qixit-auth-panel">
      {header}

      {step !== 'create' && (
        <Box sx={{ mb: 1 }} data-testid={`${testIdPrefix}-express-panel`}>
          <Box sx={{ display: 'flex', gap: 1, mb: 0.75 }}>
            <SocialContinueButton
              provider="google"
              api={api}
              context={context}
              attribution={effectiveAttribution}
              returnUrl={returnUrl}
              compact={isCompact}
              fill
              onSuccess={handleSocial}
            />
            <SocialContinueButton
              provider="microsoft"
              api={api}
              context={context}
              attribution={effectiveAttribution}
              returnUrl={returnUrl}
              compact={isCompact}
              fill
              onSuccess={handleSocial}
            />
          </Box>
          <Typography sx={{ fontSize: smallFont, textAlign: 'center', my: 0.5, color: 'text.secondary' }}>
            — OR —
          </Typography>

          <Box
            component="form"
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              if (step === 'confirm') setStep('create');
              else if (step === 'id' && identifier.trim()) void handleIdentify();
            }}
          >
            <TextField
              label={createMode ? 'Email (or username to sign in)' : 'Username or email'}
              fullWidth
              size="small"
              required
              autoFocus={variant === 'page'}
              autoComplete="username"
              value={identifier}
              onChange={(e) => {
                setIdentifier(e.target.value);
                setStep('id');
                setInfo(null);
              }}
              disabled={busy}
              sx={{ mb: isCheckout ? 0.25 : 0.5, mt: 0.25, ...(isCheckout ? { maxWidth: 240, mx: 'auto', display: 'flex' } : {}) }}
              inputProps={{ style: inputStyle, 'data-testid': 'identifier-input' }}
              InputLabelProps={{ style: { fontSize } }}
            />
            {step === 'confirm' && (
              <Typography sx={{ fontSize: isCheckout ? '12px' : isCompact ? '15px' : '1.15rem', fontWeight: 700, lineHeight: 1.3, mb: 0.5 }}>
                No Qixit account uses that email. Create one?
              </Typography>
            )}
            {step !== 'password' && (
              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="small"
                disabled={busy || !identifier.trim()}
                data-testid="identifier-continue"
                sx={{ mb: 0.5, fontSize }}
              >
                {busy ? 'Please wait…' : step === 'confirm' ? 'Create account' : 'Continue'}
              </Button>
            )}
          </Box>

          {info && (
            <Typography sx={{ fontSize: smallFont, color: 'text.secondary', mb: 0.5 }} data-testid="qixit-auth-info">
              {info}
            </Typography>
          )}

          {step === 'password' && (
            <Box component="form" onSubmit={handlePassword}>
              <TextField
                label="Password"
                type={showPassword ? 'text' : 'password'}
                fullWidth
                size="small"
                required
                autoFocus
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={busy}
                sx={{ mb: 0.5 }}
                inputProps={{ style: inputStyle, 'data-testid': `${testIdPrefix}-login-password` }}
                InputLabelProps={{ style: { fontSize } }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        onClick={() => setShowPassword((v) => !v)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button type="submit" variant="contained" fullWidth size="small" disabled={busy} sx={{ mb: 0.5, fontSize }} data-testid="qixit-auth-login">
                {busy ? 'Logging in…' : 'Log In'}
              </Button>
            </Box>
          )}

          <Typography sx={{ fontSize: isCompact ? '9px' : '12px', color: 'text.secondary' }}>
            By continuing you agree to the{' '}
            <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">
              Terms and Privacy Policy
            </a>
            .
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5, flexWrap: 'wrap' }}>
            <Link
              component="button"
              type="button"
              onClick={() => {
                const typed = identifier.trim();
                if (onForgotPassword) onForgotPassword(typed);
                else setForgotPasswordOpen(true);
              }}
              sx={{ fontSize: smallFont }}
              data-testid="identifier-forgot-password"
            >
              Forgot password
            </Link>
            <Link
              component="button"
              type="button"
              onClick={() => setForgotUsernameOpen(true)}
              sx={{ fontSize: smallFont }}
              data-testid="identifier-forgot-username"
            >
              Forgot username
            </Link>
          </Box>
        </Box>
      )}

      {step === 'create' && (
        <Box data-testid={`${testIdPrefix}-express-panel`}>
          <ExpressSignup
            api={api}
            context={context}
            attribution={effectiveAttribution}
            returnUrl={returnUrl}
            lockedEmail={identifier.trim()}
            fullRegisterUrl={fullRegisterUrl}
            onExpressDisabled={onExpressDisabled}
            compact={isCompact}
            onSuccess={handleExpress}
            onCancel={() => setStep('id')}
          />
          <Typography sx={{ fontSize: isCompact ? '9px' : '12px', color: 'text.secondary', mt: 0.5 }}>
            By continuing you agree to the{' '}
            <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">
              Terms and Privacy Policy
            </a>
            . We pick a username for you; you can change it later in Mailbox.
          </Typography>
        </Box>
      )}

      {footer}

      {!onForgotPassword && (
        <ForgotPasswordDialog
          api={api}
          open={forgotPasswordOpen}
          initialUsername={identifier.trim()}
          onClose={() => setForgotPasswordOpen(false)}
        />
      )}
      <ForgotUsernameDialog
        api={api}
        open={forgotUsernameOpen}
        initialEmail={identifier}
        onClose={() => setForgotUsernameOpen(false)}
      />
    </Box>
  );
}
